import { logger } from '@/util/logger';

export default function(id, type, settings) {
    /***
        SETTINGS:

        -settings.columns [required] array
            Each array item (column)is a javascript object with the following properties:
            "header": [required] string, the column header. This must match the cell class name for the column (e.g. rt-cell--header)
            "fixed": [optional] boolean, describes whether the column will grow to fill available space or not. If true, column will not expand.
            "minWidth": [optional] integer, px value indicating the minimum amount of available pixels at which this column should be displayed. The minWidth property is required for expandable AND hideable columns.
            "priority": [optional] integer, number indicating the order in which hideable columns should be hidden. Lowest priority columns are hidden last. Note: setting a priority indicates that the column is
                        hideable. If no priority is set, the column will be assumed required and thus never hidden by the ResponsiveTable logic

            Example columns array:
            -----------------------------------
            var feedColumns = [
                { 'header': 'select', 'fixed': true },
                { 'header': 'source', 'fixed': true },
                { 'header': 'image', 'fixed': true },
                { 'header': 'date', 'fixed': true },
                { 'header': 'category', 'minWidth': 125, 'priority': 2 },
                { 'header': 'vendor', 'minWidth': 125, 'priority': 3 },
                { 'header': 'reason', 'minWidth': 120, 'priority': 1 },
                { 'header': 'amount', 'fixed': true }
            ]

        -settings.shortDateWidth [optional] integer - specifies the window width value in pixels at which the date should be converted from mm/dd/yyyy to mm/dd/yy format. Leave blank
        -settings.members [optional] string - comma separated list of rendered datasets that belong to this responsive table. See Wallet.aspx in unmerged view for example
        -settings.isSortable [optional] - Indicates whether the dataset should be made sortable by column header. False by default
        -settings.sortMethod [optional] - if sortable, sortMethod is the column header to be sorted on initialization
        -settings.sortOrder [optional] = if sortable, sortOrder is the order (reverse or standard) in which to sort on initialization
        -settings.sortType [optional] = if sortable, sortType is the type of sort to perform upon initialization (e.g. alphabetical vs numeric)

        IMPORTANT STYLING NOTES:
        To be as performant as possible, this module uses very minimal inline style switching and relies heavily on external CSS to handle styling
        1) All responsive tables should include the template-responsive-table mixin. To set base styles. Use this mixin as a template for authoring HTML and CSS that builds on the ResponsiveTable module
        2) All columns must have their minimum width set via the "width property" in a CSS stylesheet. If a minWidth is set for that column, it should match this width exactly.
        3) Each responsive table row is a flex container, so column expansion may be set via the flex-grow property
        4) Required (always visible columns) should be visible by default, hideable columns should be hidden by default (display:none) until the the ResponsiveTable initializes.

    ***/
    // Feed cached selectors
    this.s = settings || {};
    this.id = id;

    this.type = type || 'rt';

    this.$outer = $('#' + this.id);

    this.firstRow = null;

    this.isShortDateMode = false;

    // responsive column data must be passed in settings
    this.columns = [];
    this.pinnedWidth = 0;
    this.prevTotalWidth = null;
    this.styleNode = null;
    this.columnStyles = "";
    this.numRows = 0;

    // format style node
    this.styleNode = document.createElement('style');
    this.styleNode.id = this.id + '-styles';

    // WebKit hack
    this.styleNode.appendChild(document.createTextNode(""));

    // Add the <style> element to the document head
    document.head.appendChild(this.styleNode);

    this.members = [];
    this.dataSets = {};

    if (this.s.members) {
        this.members = this.s.members.replace(/ /g, '').split(',');
    } else {
        this.members.push(this.id);
    }

    if (!this.s.columns) {
        throw new Error("No column data passed to settings object.");
    }

    // concat to kill reference to column settings array and sort by fixed/displaypriority
    this.columns = this.s.columns.concat().sort(function(a, b) {
        // fixed columns first, then ordered by display priority
        var aVal = a.priority || 0;
        var bVal = b.priority || 0;

        return sortNumeric(aVal, bVal, 1);
    });

    // Sort properties
    this.initialSort = {};
    this.currentSort = {};

    if (this.s.isSortable) {
        // save reference to initial sort config to compare against on unload.
        this.sortMethod = this.s.sortMethod !== "undefined" ? this.s.sortMethod : 'source';
        this.sortOrder = this.s.sortOrder !== "undefined" ? this.s.sortOrder : 1;
        this.sortType = this.s.sortType !== "undefined" ? this.s.sortType : 'numeric';

        this.initialSort.method = this.s.sortMethod !== "undefined" ? this.s.sortMethod : 'source';
        this.initialSort.order = this.s.sortOrder !== "undefined" ? this.s.sortOrder : 1;
        this.initialSort.type = this.s.sortType !== "undefined" ? this.s.sortType : 'numeric';

        this.currentSort.method = this.s.sortMethod !== "undefined" ? this.s.sortMethod : 'source';
        this.currentSort.order = this.s.sortOrder !== "undefined" ? this.s.sortOrder : 1;
        this.currentSort.type = this.s.sortType !== "undefined" ? this.s.sortType : 'numeric';
    }

    this.getTotalWidth = function() {
        var r = window.getComputedStyle(this.firstRow);
        // calculate total width from container width global because body and component width
        // are unreliable when width is pinned during debounced layout.
        var rowPaddingLeft = parseInt(r.paddingLeft) || 0;
        var rowPaddingRight = parseInt(r.paddingRight) || 0;

        return Math.min(this.$outer.width(), containerWidth - rowPaddingLeft - rowPaddingRight);
    };

    this.getSortedRows = function($rows, headerName, sortOrder, sortType, secondarySort) {
        var aVal;
        var bVal;
        var aVal2;
        var bVal2;

        this.currentSort.method = headerName;
        this.currentSort.order = sortOrder;
        this.currentSort.type = sortType;

        if (sortType === 'numeric') {
            return $rows.sort(function(a, b) {
                var aCell = a.querySelector('.js-col-' + headerName);
                var bCell = b.querySelector('.js-col-' + headerName);

                aVal = parseFloat(aCell.getAttribute('data-sort-value'), 10);
                bVal = parseFloat(bCell.getAttribute('data-sort-value'), 10);
                if (secondarySort) {
                    aVal2 = a.querySelector('.js-col-' + secondarySort).getAttribute('data-sort-value');
                    bVal2 = b.querySelector('.js-col-' + secondarySort).getAttribute('data-sort-value');

                    return sortNumericWithSecondary(aVal, bVal, sortOrder, aVal2, bVal2, 'numeric');
                }

                return sortNumeric(aVal, bVal, sortOrder);
            });
        }

        if (sortType === 'alpha') {
            return $rows.sort(function(a, b) {
                var aCell = a.querySelector('.js-col-' + headerName);
                var bCell = b.querySelector('.js-col-' + headerName);
                try {
                    aVal = aCell.getAttribute('data-sort-value') ? aCell.getAttribute('data-sort-value').toLowerCase() : aCell.textContent.trim().toLowerCase();
                    bVal = bCell.getAttribute('data-sort-value') ? bCell.getAttribute('data-sort-value').toLowerCase() : bCell.textContent.trim().toLowerCase();

                } catch (err) {
                    aVal = '';
                    bVal = '';
                }

                if (secondarySort) {
                    aVal2 = aCell.parentNode.querySelector('.js-col-' + secondarySort).getAttribute('data-sort-value');
                    bVal2 = bCell.parentNode.querySelector('.js-col-' + secondarySort).getAttribute('data-sort-value');

                    return sortAlphaWithSecondary(aVal, bVal, sortOrder, aVal2, bVal2, 'alpha');
                }

                return sortAlpha(aVal, bVal, sortOrder);
            });
        }
    }; // end create sort map


    this.sortByColumn = function(dataSet, sortMethod, sortOrder, sortType, isInit) {
        // fail if no cells to sort by.
        if (!dataSet.$body.find('.js-col-' + sortMethod).length) return;

        // we are always secondary sorting by date, so ok to hardcode that in, until when/if we change that
        dataSet.$rows = this.getSortedRows(dataSet.$rows, sortMethod, sortOrder, sortType, 'date');
        dataSet.sortPending = true;

        this.currentSort.order = sortOrder;

        this.doSaveSortPrefPost();
    };

    this.unloadSortedColumns = function(isInit) {
        var s;
        for (var set in this.dataSets) {
            s = this.dataSets[set];

            if (s.sortPending) {
                s.$body.html(s.$rows);
                s.sortPending = false;
            }
        }

        if (typeof this.afterSort === 'function') {
            this.afterSort(isInit);
        }
    };


    this.cacheSelectors = function() {
        this.$outer = $('#' + this.id);
        this.$rows = this.$outer.find('.js-' + this.type + '-row');
        this.numRows = this.$rows.length;

        this.members.forEach(function(id, index) {
            // make sure that dataset member exists
            if (!$('#' + id).length) {
                logger.warn('No element found for Member id: ' + id + ' for Feed ' + this.id);
                return;
            }

            let set = {};

            set.$outer = $('#' + id);

            set.$body = set.$outer.find('.js-' + this.type + '-body') || set.$outer;
            set.$rows = set.$body.find('.js-' + this.type + '-row');
            this.dataSets[id] = set;

        }, this);

        this.columns.forEach(function(col) {
            col.cells = this.$outer[0].querySelectorAll('.js-col-' + col.header);
            col.key = col.cells[0];
            col.allocation = col.allocation || 1;

            // Log warning if no cells found for this column
            if (!col.key) {
                logger.warn('No cells found for column: "' + col.header + '". Column may not be rendered properly.');
            }

            if (col.priority > 0 && (!col.minWidth && !col.fixed)) {
                throw new Error('minWidth not for hideable column, "' + col.header + '"');
            }
        }, this);

        if (typeof this.cacheAdditionalSelectors === 'function') this.cacheAdditionalSelectors();

    };


    this.convertToShortDates = function() {
        var $dateCells = this.$outer.find('.js-col-date');

        $dateCells.addClass('has-short-date');

        $dateCells.not('th').each(function(i) {
            var shortDate = this.getAttribute('data-date-short') || '';
            this.textContent = shortDate;
        });

    };

    this.convertToExpandedDates = function() {
        var $dateCells = this.$outer.find('.js-col-date');

        $dateCells.removeClass('has-short-date');

        $dateCells.not('th').each(function(i) {
            var longDate = this.getAttribute('data-date-long') || '';
            this.textContent = longDate;
        });
    };


    this.doDateDisplayLogic = function(totalWidth) {
        if (!this.isShortDateMode) {
            // don't show compact dates if there is room to show expanded dates.
            if (totalWidth > this.s.shortDateWidth) return;

            this.convertToShortDates();
            this.isShortDateMode = true;
        } else {
            // don't show expanded dates if there is not room for them.
            if (totalWidth < this.s.shortDateWidth) return;

            this.convertToExpandedDates();
            this.isShortDateMode = false;
        }
    };

    this.sizeColumns = function(immediate, resizeUp) {
        // abort here if table does not have any rows
        if (this.numRows < 1) return;

        var totalWidth = this.getTotalWidth();

        if (this.s.shortDateWidth) {
            this.doDateDisplayLogic(totalWidth);
        }

        if (typeof this.beforeSizeColumns === 'function') {
            this.beforeSizeColumns();
        }

        this.distributeAvailableWidth(totalWidth);

    };

    this.distributeAvailableWidth = function(availableWidth, isDebounced) {
        var that = this;

        var col;
        var i = 0;
        var len = 0;
        var minWidth = 0;

        // loop through all columns and display as many as possible
        for (i = 0, len = this.columns.length; i < len; i++) {
            col = this.columns[i];

            if (!col) {
                continue;
            }

            const hasStaticMinWidth = (typeof col.minWidth !== 'undefined' && 
                col.minWidth !== null && 
                !isNaN(col.minWidth) && 
                col.minWidth >= 0)

            const hasValidMinWidth = hasStaticMinWidth || col.key;

            // minimum width can be read from clientWidth property for fixed columns
            // check if col.key exists before accessing clientWidth to prevent TypeError
            if (!hasValidMinWidth) {
                continue;
            }

            minWidth = col.minWidth > 0 ? col.minWidth : col.key.clientWidth;

            if (availableWidth > minWidth) {
                this.showColumn(col)
                availableWidth -= minWidth;
            } else if (col.priority === 1) {
                if (availableWidth < 1) {
                    logger.warn('Not enough space available to show required column: "' + col.header + '"');
                    this.hideColumn(col);
                    continue;
                }

                if (col.fixed) logger.warn('Not enough space to display fixed column, "' + col.header + '" at specified size. Shrinking to fit.')

                this.showColumn(col, availableWidth);
                availableWidth = 0;
            } else {
                this.hideColumn(col);
            }

        }

        // apply columnstyles queued via this.showColumn and this.hideColumn methods
        this.styleNode.innerHTML = this.columnStyles;
        this.columnStyles = "";

        if (typeof this.afterDistributeAvailableWidth === 'function') {
            this.afterDistributeAvailableWidth();
        }

    };

    this.showColumn = function(col, width) {
        this.columnStyles += '.' + this.type + '_cell--' + col.header + ' {display:table-cell;';

        if (width) this.columnStyles += 'max-width:' + width + "px" + ';';

        this.columnStyles += '}';

        if (typeof this.afterShowColumn === 'function' && !col.fixed) {
            this.afterShowColumn(col);
        }
    };

    this.hideColumn = function(col) {
        this.columnStyles += '.' + this.type + '_cell--' + col.header + ' {display:none;}';

        if (typeof this.afterHideColumn === 'function' && !col.fixed) {
            this.afterHideColumn(col);
        }
    };

    this.saveSortPrefTimer = null;

    this.doSaveSortPrefPost = debounce(function() {
        var str = "WalletService.aspx?";
        var doPost = false;

        for (var prop in this.initialSort) {
            if (this.initialSort.hasOwnProperty(prop)) {
                if (this.initialSort[prop] !== this.currentSort[prop]) {
                    str += 'walletSort' + prop.toLowerCase().capitalizeFirstLetter() + '=' + this.currentSort[prop] + '&';
                    // update initial sort to reflect the new info we stored in the db
                    this.initialSort[prop] = this.currentSort[prop];
                }
                doPost = true;
            }
        }

        if (doPost) {
            $.get(str);
        }

    }, 300);

    this.handleResize = function() {
        var resizeUp = this.pinnedWidth && this.pinnedWidth <= getWindowWidth();
        this.sizeColumns(false, resizeUp);
    };

    this.init = function() {
        var that = this;
        var set;

        // don't do init if outer elem doesn't exist
        if (!$('#' + id).length) return;

        // prevent reattachment of responsive table events.
        this.cacheSelectors();

        // abort here if no rows as the empty state will be shown instead
        if (this.numRows < 1) {
            layoutQueue.remove('handleResize', this)
            return;
        }

        this.firstRow = this.$outer[0].querySelector('.js-' + this.type + '-row');

        if (typeof this.beforeInit === 'function') {
            this.beforeInit(this);
        }

        this.sizeColumns(true);

        // Sorting Config
        if (this.s.isSortable) {
            // sort all datasets
            for (set in this.dataSets) {
                this.sortByColumn(this.dataSets[set], this.initialSort.method, this.initialSort.order, this.initialSort.type, true);
            }
            for (set in this.dataSets) {
                this.unloadSortedColumns(this.dataSets[set]);
            }

            this.$outer.on('click.rt', '.js-sort-by-header', function(e) {
                var d = $(this).data();
                var sortColumn = $(this).parent().data().header;
                var sortOrder = d.sortDefault;
                var dataSet = that.dataSets[$(this).parents('.js-' + that.type + '-data').attr('id')];

                if (that.currentSort.method === sortColumn) {
                    sortOrder = that.currentSort.order * -1;
                }

                that.sortByColumn(dataSet, sortColumn, sortOrder, d.sortType);
                that.unloadSortedColumns(true);
            });
        }

        // If the responsive table has selectable items, attach events to control checkbox/radio interaction
        if (this.$outer.find('.js-' + that.type + '-select-all').length) {

            this.$outer.on('change', '.js-' + that.type + '-select-all', function(e) {
                var value = this.checked;
                var $tableData = $(this).parents('.js-' + that.type + '-data');

                $tableData.find('.js-' + that.type + '-select')
                    .each(function() {
                        if (!$(this).attr('disabled')) {
                            $(this).attr('checked', value)
                            $(this).prop('checked', value);
                        }
                        if ($(this).is("span")) {
                            var nestedCheckbox = $(this).find('[type=checkbox]')
                            if (!nestedCheckbox.attr('disabled')) {
                                nestedCheckbox.attr('checked', value)
                                nestedCheckbox.prop('checked', value);
                            }
                        }
                    });
            });

        }
        if (this.$outer.find('.js-' + that.type + '-select').length) {
            this.$outer.on('change', '.js-' + that.type + '-select', function(e) {
                if (this.type === 'radio') {
                    that.$outer.find('.js-' + that.type + '-select').not($(this)).each(function() {
                        this.removeAttribute('checked');
                    });
                }

                if (this.checked) {
                    this.setAttribute('checked', 'checked');
                } else {
                    this.removeAttribute('checked');
                }
            });

        };

        if (typeof this.afterInit === 'function') {
            this.afterInit(this);
        }

        layoutQueue.insertAfter('resizeExpEditControls', 'handleResize', this);

        that.$outer.find('.js-' + that.type + '-body').addClass('is-loaded');

    };
}